/**
 * v2 (26/10/2026) — the countries one paste goes to (user: "you can paste into several countries
 * at the same time but you have to specify the campaign group of course"): one target per
 * country — its campaign group and its own choices — and the one shown. The board's country
 * comes first, on the group open there; an added country picks its group when its groups load.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useState } from 'react';
import type { GroupCopyChoices } from '@trafficpulse/shared';

import { forNewTarget } from '../paste-choices';

export interface PasteTarget {
  /** The country key — one target per country. */
  id: string;
  countryKey: string;
  /** The campaign group it lands in; null = not picked yet. */
  groupKey: string | null;
  choices: GroupCopyChoices;
}

const fresh = (countryKey: string, groupKey: string | null): PasteTarget => ({ id: countryKey, countryKey, groupKey, choices: {} });

export function usePasteTargets(first: { countryKey: string; groupKey: string | null }) {
  const [targets, setTargets] = useState<PasteTarget[]>(() => [fresh(first.countryKey, first.groupKey)]);
  const [activeId, setActiveId] = useState(first.countryKey);
  const patch = (id: string, change: (t: PasteTarget) => PasteTarget) =>
    setTargets((ts) => ts.map((t) => (t.id === id ? change(t) : t)));
  const remove = (ids: string[]) => {
    setTargets((ts) => {
      const left = ts.filter((t) => !ids.includes(t.id));
      if (ids.includes(activeId) && left[0]) setActiveId(left[0].id);
      return left;
    });
  };
  return {
    targets,
    active: targets.find((t) => t.id === activeId) ?? targets[0] ?? null,
    setActive: setActiveId,
    add: (countryKey: string) => {
      setTargets((ts) => (ts.some((t) => t.id === countryKey) ? ts : [...ts, fresh(countryKey, null)]));
      setActiveId(countryKey);
    },
    remove,
    setGroup: (id: string, groupKey: string) => patch(id, (t) => ({ ...t, groupKey, choices: forNewTarget(t.choices) })),
    setChoices: (id: string, choices: GroupCopyChoices) => patch(id, (t) => ({ ...t, choices })),
  };
}
